import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { createClient } from '@/lib/supabase';
import { apiGet } from '@/lib/api';
import type { User } from '@hypema/shared';
import type { Session } from '@supabase/supabase-js';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  needsOnboarding: boolean;
}

export interface AuthContextValue extends AuthState {
  signInWithGoogle: (redirectAfterLogin?: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-fetch user from backend (call after tenant creation) */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const isAuthBypass = import.meta.env.VITE_AUTH_BYPASS === 'true';

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [supabase] = useState(() => createClient());
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    isAuthenticated: false,
    needsOnboarding: false,
  });

  // Fetch the HyPeMa user record from our backend; fall back to session email
  const fetchUser = useCallback(
    async (session: Session): Promise<{ user: User; needsOnboarding: boolean }> => {
      const fallbackUser: User = {
        id: session.user.id,
        email: session.user.email || '',
        tenantId: '',
        role: 'member',
        createdAt: session.user.created_at || '',
      };

      try {
        localStorage.setItem('auth_token', session.access_token);
        const data = await apiGet<User | null>('/auth/me');
        if (data) {
          return { user: data, needsOnboarding: false };
        }
        // API succeeded but no user record → needs onboarding, use session email
        return { user: fallbackUser, needsOnboarding: true };
      } catch {
        // API unreachable — use session email as fallback
        return { user: fallbackUser, needsOnboarding: false };
      }
    },
    [],
  );

  useEffect(() => {
    // Dev bypass mode
    if (isAuthBypass) {
      setState({
        user: {
          id: 'dev-user-001',
          tenantId: 'dev-tenant-001',
          email: 'dev@hypema.io',
          role: 'admin',
          createdAt: new Date().toISOString(),
        },
        session: null,
        loading: false,
        isAuthenticated: true,
        needsOnboarding: false,
      });
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const { user, needsOnboarding } = await fetchUser(session);
        setState({
          user,
          session,
          loading: false,
          isAuthenticated: true,
          needsOnboarding,
        });
      } else {
        localStorage.removeItem('auth_token');
        setState((prev) => ({ ...prev, loading: false }));
      }
    });

    // Listen for auth state changes (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const { user, needsOnboarding } = await fetchUser(session);
        setState({
          user,
          session,
          loading: false,
          isAuthenticated: true,
          needsOnboarding,
        });
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem('auth_token');
        setState({
          user: null,
          session: null,
          loading: false,
          isAuthenticated: false,
          needsOnboarding: false,
        });
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // Update the stored token
        localStorage.setItem('auth_token', session.access_token);
        setState((prev) => ({ ...prev, session }));
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase, fetchUser]);

  const signInWithGoogle = useCallback(async (redirectAfterLogin?: string) => {
    // If a redirect path is provided (e.g. from OAuth consent flow),
    // pass it as a query param to the callback so we can redirect after auth
    const callbackUrl = redirectAfterLogin
      ? `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectAfterLogin)}`
      : `${window.location.origin}/auth/callback`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: callbackUrl,
      },
    });
    if (error) throw error;
  }, [supabase]);

  const refreshUser = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { user, needsOnboarding } = await fetchUser(session);
      setState((prev) => ({ ...prev, user, needsOnboarding }));
    }
  }, [supabase, fetchUser]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('auth_token');
    navigate('/login');
  }, [supabase, navigate]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithGoogle,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
